// /drugs/side-effects.ts
// QUANTITIES ARE PRE-VERIFIED LOOKUPS. DO NOT CALCULATE.
//
// Side effects extracted verbatim from NHS England Pharmacy First PGDs
// ("Identification & management of adverse reactions" section).
// Source: PRN010101a–PRN010107d, valid from 31/01/2024, expiry 30/01/2027
//
// Frequency mapping follows SPC/BNF terminology as stated in each PGD:
//   "very common or common" → "common"
//   "uncommon"              → "uncommon"
//   "rare" / "rare but serious" → "rare"
//
// DO NOT add, remove, or reclassify effects without reference to the
// source PGD document. These are clinical facts, not engineering decisions.

export type SideEffectFrequency = "common" | "uncommon" | "rare";

export interface SideEffect {
  effect: string;
  frequency: SideEffectFrequency;
}

// ─────────────────────────────────────────────────────────────────────────────
// UTI
// Source: PRN010101a — Nitrofurantoin PGD (v1.2)
// ─────────────────────────────────────────────────────────────────────────────

export const nitrofurantoinSideEffects: SideEffect[] = [
  { effect: "Nausea", frequency: "common" },
  { effect: "Vomiting", frequency: "common" },
  { effect: "Diarrhoea", frequency: "common" },
  { effect: "Loss of appetite", frequency: "common" },
  { effect: "Headaches", frequency: "common" },
  { effect: "Dizziness", frequency: "common" },
  { effect: "Drowsiness", frequency: "common" },
  { effect: "Discoloured dark yellow or brown urine", frequency: "common" },
  // Rare but serious — pharmacist must advise patient to seek urgent care
  {
    effect: "Respiratory reactions (trouble breathing, shortness of breath, lingering cough, coughing up blood or mucus)",
    frequency: "rare",
  },
  {
    effect: "Hepatic reactions (yellowing of skin or eyes, upper right abdominal pain, dark urine, pale or grey stools, itching or joint pain and swelling)",
    frequency: "rare",
  },
  { effect: "Peripheral neuropathy", frequency: "rare" },
];

// ─────────────────────────────────────────────────────────────────────────────
// SHINGLES
// Source: PRN010102a — Aciclovir PGD (v1.0)
// ─────────────────────────────────────────────────────────────────────────────

export const aciclovirSideEffects: SideEffect[] = [
  { effect: "Diarrhoea", frequency: "common" },
  { effect: "Vomiting and nausea", frequency: "common" },
  { effect: "Abdominal pain", frequency: "common" },
  { effect: "Headache", frequency: "common" },
  { effect: "Dizziness", frequency: "common" },
  { effect: "Fever", frequency: "common" },
  { effect: "Fatigue", frequency: "common" },
  {
    effect: "Skin rashes or reactions (including photosensitivity and urticaria)",
    frequency: "common",
  },
  { effect: "Anaphylaxis (delayed or immediate)", frequency: "rare" },
];

// Source: PRN010102b — Valaciclovir PGD (v1.0)

export const valaciclovirSideEffects: SideEffect[] = [
  { effect: "Diarrhoea", frequency: "common" },
  { effect: "Vomiting and nausea", frequency: "common" },
  { effect: "Abdominal pain", frequency: "common" },
  { effect: "Headache", frequency: "common" },
  { effect: "Dizziness", frequency: "common" },
  { effect: "Fever", frequency: "common" },
  { effect: "Fatigue", frequency: "common" },
  {
    effect: "Skin rashes/reactions (including photosensitivity and urticaria)",
    frequency: "common",
  },
  // DRESS is explicitly highlighted in the PGD as rare but requiring special counselling
  {
    effect: "DRESS — drug reaction with eosinophilia and systemic symptoms (high fever, morbilliform eruption, haematological abnormalities, lymphadenopathy, inflammation of internal organs)",
    frequency: "rare",
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// IMPETIGO — topical
// Source: PRN010103a — Hydrogen peroxide 1% cream Protocol (v1.0)
// Note: PGD lists only ONE SPC-listed side effect for this product.
// ─────────────────────────────────────────────────────────────────────────────

export const hydrogenPeroxideCreamSideEffects: SideEffect[] = [
  {
    effect: "Paraesthesia (mild sensation of burning after application)",
    frequency: "common",
  },
  // Excipient-related — salicylic acid and propylene glycol are noted skin irritants
  { effect: "Local skin irritation (from excipients)", frequency: "uncommon" },
];

// Source: PRN010103b — Fusidic acid cream PGD (v1.0)
// PGD labels all listed effects as "uncommon"

export const fusidicAcidCreamSideEffects: SideEffect[] = [
  {
    effect: "Dermatitis (including contact dermatitis and eczema)",
    frequency: "uncommon",
  },
  {
    effect: "Rash (erythematous, pustular, vesicular, maculo-papular and papular)",
    frequency: "uncommon",
  },
  { effect: "Pruritus", frequency: "uncommon" },
  { effect: "Erythema", frequency: "uncommon" },
  {
    effect: "Application site pain (including skin burning sensation)",
    frequency: "uncommon",
  },
  { effect: "Application site irritation", frequency: "uncommon" },
];

// ─────────────────────────────────────────────────────────────────────────────
// IMPETIGO / INFECTED INSECT BITES — oral antibiotics
// Source: PRN010103c / PRN010104a — Flucloxacillin PGD (v1.0)
// PGD labels all listed effects as "very common or common"
// ─────────────────────────────────────────────────────────────────────────────

export const flucloxacillinSideEffects: SideEffect[] = [
  { effect: "Diarrhoea", frequency: "common" },
  { effect: "Nausea", frequency: "common" },
  { effect: "Skin rash", frequency: "common" },
  { effect: "Hypersensitivity reactions", frequency: "common" },
  { effect: "Vomiting", frequency: "common" },
  {
    effect: "Thrombocytopenia (low levels of platelets in the blood)",
    frequency: "common",
  },
  { effect: "Anaphylaxis (delayed or immediate)", frequency: "rare" },
];

// Source: PRN010103d / PRN010104b / PRN010105b / PRN010106d / PRN010107c
// — Clarithromycin PGD (v1.0)
// PGD labels all listed effects as "very common or common"

export const clarithromycinSideEffects: SideEffect[] = [
  {
    effect: "Gastrointestinal discomfort (dyspepsia, diarrhoea, nausea, vomiting, abdominal pain, pancreatitis)",
    frequency: "common",
  },
  { effect: "Abnormal liver function tests", frequency: "common" },
  { effect: "Decreased appetite", frequency: "common" },
  { effect: "Dizziness", frequency: "common" },
  { effect: "Headache", frequency: "common" },
  { effect: "Hearing impairment", frequency: "common" },
  { effect: "Insomnia", frequency: "common" },
  {
    effect: "Skin rashes/reactions (including hyperhidrosis and paraesthesia)",
    frequency: "common",
  },
  { effect: "Taste altered", frequency: "common" },
  { effect: "Vasodilation", frequency: "common" },
  { effect: "Vision disorders", frequency: "common" },
  { effect: "Anaphylaxis (delayed or immediate)", frequency: "rare" },
];

// Source: PRN010103e / PRN010104c / PRN010105c / PRN010106f / PRN010107d
// — Erythromycin PGD (v1.0)
// PGD labels all listed effects as "very common or common"

export const erythromycinSideEffects: SideEffect[] = [
  {
    effect: "Gastrointestinal discomfort (diarrhoea, nausea, vomiting, pancreatitis)",
    frequency: "common",
  },
  { effect: "Decreased appetite", frequency: "common" },
  { effect: "Dizziness", frequency: "common" },
  { effect: "Headache", frequency: "common" },
  { effect: "Hearing impairment", frequency: "common" },
  { effect: "Insomnia", frequency: "common" },
  {
    effect: "Skin rashes/reactions (including paraesthesia)",
    frequency: "common",
  },
  { effect: "Taste altered", frequency: "common" },
  { effect: "Vasodilation", frequency: "common" },
  { effect: "Vision disorders", frequency: "common" },
  { effect: "Anaphylaxis (delayed or immediate)", frequency: "rare" },
];

// ─────────────────────────────────────────────────────────────────────────────
// SORE THROAT / SINUSITIS — penicillin
// Source: PRN010105a / PRN010106c — Phenoxymethylpenicillin (Penicillin V) PGD (v1.0)
// PGD labels all listed effects as "very common or common"
// ─────────────────────────────────────────────────────────────────────────────

export const phenoxymethylpenicillinSideEffects: SideEffect[] = [
  { effect: "Diarrhoea", frequency: "common" },
  { effect: "Nausea", frequency: "common" },
  { effect: "Skin rash", frequency: "common" },
  { effect: "Hypersensitivity reactions", frequency: "common" },
  { effect: "Vomiting", frequency: "common" },
  {
    effect: "Thrombocytopenia (low levels of platelets in the blood)",
    frequency: "common",
  },
  { effect: "Anaphylaxis (delayed or immediate)", frequency: "rare" },
];

// ─────────────────────────────────────────────────────────────────────────────
// SINUSITIS — intranasal corticosteroids
// Source: PRN010106a — Fluticasone furoate nasal spray PGD (v1.0)
// PGD labels all listed effects as "very common or common"
// ─────────────────────────────────────────────────────────────────────────────

export const fluticasoneSideEffects: SideEffect[] = [
  { effect: "Epistaxis (nosebleed)", frequency: "common" },
  { effect: "Headache", frequency: "common" },
  { effect: "Throat irritation", frequency: "common" },
  { effect: "Nasal ulceration", frequency: "common" },
  { effect: "Dyspnoea", frequency: "common" },
  { effect: "Altered smell", frequency: "common" },
  { effect: "Altered taste", frequency: "common" },
  { effect: "Anaphylaxis (delayed or immediate)", frequency: "rare" },
];

// Source: PRN010106b — Mometasone nasal spray PGD (v1.0)
// PGD labels all listed effects as "very common or common"

export const mometasoneSideEffects: SideEffect[] = [
  { effect: "Epistaxis (nosebleed)", frequency: "common" },
  { effect: "Headache", frequency: "common" },
  { effect: "Nasal burning", frequency: "common" },
  { effect: "Pharyngitis", frequency: "common" },
  { effect: "Nasal irritation", frequency: "common" },
  { effect: "Throat irritation", frequency: "common" },
  { effect: "Nasal ulceration", frequency: "common" },
  { effect: "Upper respiratory tract infection", frequency: "common" },
  { effect: "Altered smell", frequency: "common" },
  { effect: "Altered taste", frequency: "common" },
  { effect: "Anaphylaxis (delayed or immediate)", frequency: "rare" },
];

// Source: PRN010106e — Doxycycline PGD (v1.0)
// PGD labels all listed effects as "common"

export const doxycyclineSideEffects: SideEffect[] = [
  { effect: "Diarrhoea", frequency: "common" },
  { effect: "Hypersensitivity reactions", frequency: "common" },
  { effect: "Headache", frequency: "common" },
  { effect: "Nausea and vomiting", frequency: "common" },
  { effect: "Photosensitivity skin reactions", frequency: "common" },
  {
    effect: "Rash (maculopapular, erythematous, Henoch-Schönlein purpura)",
    frequency: "common",
  },
  { effect: "Urticaria", frequency: "common" },
  { effect: "Hypotension", frequency: "common" },
  { effect: "Pericarditis", frequency: "common" },
  { effect: "Tachycardia", frequency: "common" },
  { effect: "Dyspnoea", frequency: "common" },
  { effect: "Peripheral oedema", frequency: "common" },
  { effect: "Anaphylaxis (delayed or immediate)", frequency: "rare" },
];

// ─────────────────────────────────────────────────────────────────────────────
// ACUTE OTITIS MEDIA
// Source: PRN010107a — Phenazone/lidocaine ear drops (Otigo®) PGD (v1.0)
// PGD lists all effects as "rare"
// ─────────────────────────────────────────────────────────────────────────────

export const phenazoneWithLidocaineSideEffects: SideEffect[] = [
  {
    effect: "Local allergic reactions (itching, maculopapular rash)",
    frequency: "rare",
  },
  {
    effect: "Auditory canal hyperaemia (increased blood flow to the auditory canal)",
    frequency: "rare",
  },
];

// Source: PRN010107b — Amoxicillin PGD (v1.0)
// PGD labels all listed effects as "common or very common"

export const amoxicillinSideEffects: SideEffect[] = [
  { effect: "Diarrhoea", frequency: "common" },
  { effect: "Nausea", frequency: "common" },
  { effect: "Skin rash", frequency: "common" },
  { effect: "Hypersensitivity reactions", frequency: "common" },
  { effect: "Vomiting", frequency: "common" },
  {
    effect: "Thrombocytopenia (low levels of platelets in the blood)",
    frequency: "common",
  },
  { effect: "Anaphylaxis (delayed or immediate)", frequency: "rare" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Convenience lookup by drug ID
// Keys match the drugId convention used in /drugs/*.ts
// ─────────────────────────────────────────────────────────────────────────────

export const SIDE_EFFECTS_BY_DRUG: Record<string, SideEffect[]> = {
  nitrofurantoin: nitrofurantoinSideEffects,
  aciclovir: aciclovirSideEffects,
  valaciclovir: valaciclovirSideEffects,
  hydrogen_peroxide_cream: hydrogenPeroxideCreamSideEffects,
  fusidic_acid_cream: fusidicAcidCreamSideEffects,
  flucloxacillin: flucloxacillinSideEffects,
  clarithromycin: clarithromycinSideEffects,
  erythromycin: erythromycinSideEffects,
  phenoxymethylpenicillin: phenoxymethylpenicillinSideEffects,
  fluticasone_furoate_nasal_spray: fluticasoneSideEffects,
  mometasone_furoate_nasal_spray: mometasoneSideEffects,
  doxycycline: doxycyclineSideEffects,
  phenazone_lidocaine_ear_drops: phenazoneWithLidocaineSideEffects,
  amoxicillin: amoxicillinSideEffects,
} as const;
